import { useSyncExternalStore } from 'react';

import type { DirectionBlindReason } from '../hooks/useDirectionChangeDetection';

/**
 * Publicación del estado del detector de cambio de sentido.
 *
 * ── Por qué existe ─────────────────────────────────────────────────────────
 *
 * El detector pasa a vivir en el `TimerOverlay` (cabecera), o sea en toda la
 * app. Pero su aviso de «estoy ciego» —sin malla, o fuera del trazado— se
 * pinta bajo el mapa de la pantalla del recorrido, que es un HERMANO de la
 * cabecera, no un descendiente: un contexto de React declarado dentro del
 * overlay no llega ahí.
 *
 * 🔴 Y no vale montar el detector dos veces. Cada instancia registra el corte
 * por su cuenta (`execute`), así que dos instancias parten el tramo dos veces.
 *
 * De ahí este almacén mínimo: una instancia publica, quien quiera lee. Se
 * queda fuera de Redux a propósito — es un dato efímero de un solo productor,
 * y no necesita acciones, reducer ni persistencia.
 */

let blindReason: DirectionBlindReason | null = null;
const listeners = new Set<() => void>();

export function setDirectionBlindReason(reason: DirectionBlindReason | null): void {
  if (blindReason === reason) return;
  blindReason = reason;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): DirectionBlindReason | null {
  return blindReason;
}

/** Motivo por el que el detector no puede opinar, o `null` si ve bien. */
export function useDirectionBlindReason(): DirectionBlindReason | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// ── La vía de APOYO que pisa la patrulla (2026-09-23) ───────────────────
//
// Con el aviso «fuera del trazado», la vía de su concesión en la que está —si
// es una— para que el aviso la nombre y ofrezca añadirla al recorrido. Mismo
// productor único que el motivo de arriba: la instancia del detector del
// `TimerOverlay`.

let offRouteRoadId: string | null = null;
const offRouteListeners = new Set<() => void>();

export function setDirectionOffRouteRoad(roadId: string | null): void {
  if (offRouteRoadId === roadId) return;
  offRouteRoadId = roadId;
  offRouteListeners.forEach((l) => l());
}

function subscribeOffRoute(listener: () => void): () => void {
  offRouteListeners.add(listener);
  return () => {
    offRouteListeners.delete(listener);
  };
}

function getOffRouteSnapshot(): string | null {
  return offRouteRoadId;
}

/** La vía de apoyo que pisa la patrulla fuera del recorrido, o `null`. */
export function useDirectionOffRouteRoad(): string | null {
  return useSyncExternalStore(
    subscribeOffRoute,
    getOffRouteSnapshot,
    getOffRouteSnapshot
  );
}
